import { generatePath } from 'react-router-dom';

import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';

import { fetchMethod } from '../apiFetch';
import apiInstance from '../index';

import {
    Applicant,
    ApplicantActivityResponse,
    ApplicantDueDiligenceData,
    DecisionDetailStatusCode,
    DecisionDetailStatuses,
    Document,
    FileType,
    PointOfContact,
    RelatedAccountsResponse,
} from '../../../pages/Applicant/Applicant.types';
import { ApplicantSubmissionSummary } from '../../../pages/Applicant/components/ApplicantSubmissions/ApplicantSubmissions.types';
import { VendorStatusResponse } from '../../../pages/Applicant/components/VendorStatusTable/VendorStatusTable.types';
import { PartnersNextStepResponse } from '../../dtos/response/applicantResponse';
import { APIRoutes } from '../../routes';
import { Config } from '../../utils/config';

export interface ApplicantResubmitRequest {
    applicantId: string;
    resetOverride: boolean;
    products?: ProductNames[];
}

export interface ApplicationStatusRequest {
    applicantId: string;
    product: ProductNames;
    status: DecisionDetailStatuses;
    reason?: string;
    notes?: string;
}

export interface AbandonApplicantRequest {
    applicantId: string;
    product: ProductNames;
    reason: string;
}

export type SortableColumn = 'statusAge' | 'applicationAge' | 'submittedAt';
export type SortOrder = 'asc' | 'desc';

export enum ProductReviewType {
    Unknown = 0,
    Bundle = 1,
    LoadPay = 2,
    Factoring = 3,
    Fuel = 4,
}

export enum OpenConditionsFilter {
    Unknown = 0,
    HasOpen = 1,
    NoneOpen = 2,
}

export interface GetApplicantsFilter {
    assigneeEmails?: string[] | null;
    businessNames?: string[] | null;
    createdAtStart?: string | null;
    createdAtEnd?: string | null;
    emails?: string[] | null;
    feins?: string[] | null;
    includeUnassigned?: boolean;
    mcNumbers?: string[] | null;
    openConditionsFilter?: OpenConditionsFilter;
    ownerNames?: string[] | null;
    personas?: number[] | null;
    phones?: string[] | null;
    productNames?: number[] | null;
    productReviewType?: ProductReviewType;
    sortBy?: SortableColumn;
    sortOrder?: SortOrder;
    statusFilter: DecisionDetailStatusCode[] | null;
    usDot?: string;
}

export interface ApplicantProduct {
    applicationAge: string;
    applicationStatus: string;
    productName: string;
    statusAge: string;
    submittedAt: string;
    openConditionCount: number;
}

export interface ApplicantProductReview {
    applicationStatus: string;
    isReviewReady: boolean;
    productName: string;
}

export interface ApplicantResponse {
    applicantId: string;
    carrierName: string;
    usDOT: string;
    partnerId: string;
    assigneeEmails: string;
    assigneeCount: number;
    factoringAssigneeEmails: string;
    loadPayAssigneeEmails: string;
    tcsFuelAssigneeEmails: string;
    lastUpdated: string;
    products: ApplicantProduct[];
    readyForReview?: ApplicantProductReview[] | null;
}

export interface InternalNoteRequest {
    applicantId: string;
    note: string;
}

export interface GenerateRecoveryLinkRequest {
    applicantId: string;
    products: ProductNames[];
}

export interface GenerateRecoveryLinkResponse {
    recoveryUrl: string;
    passcode?: string;
}

export interface OriginationFile {
    id: string;
    externalFileId: string;
    type: number;
    filePath: string;
    presignedUrl: string;
    originalFilename: string;
}

export interface ConfirmApplicantDocRequest {
    applicantId: string;
    body: {
        presignedUploadUrl: string;
        path: string;
        fileName: string;
        fileType?: FileType;
        uploadedBy?: string;
    };
}

export interface ApplicantDocPresignRequest {
    applicantId: string;
    fileName: string;
    contentType: string;
}

export interface ApplicantDocResponse {
    url: string;
    date: Date;
    path: string;
}

export interface ApplicantTagResponse {
    entityName: string;
    workflowName: string;
    evaluationToken: string;
    tags: TagDto[];
}

export interface ReasonCodeDto {
    code: string;
    description: string;
    serviceName: string;
}

export interface ProviderReasonCodes {
    providerName: string;
    reasonCodes: ReasonCodeDto[];
}

export interface ServiceReasonCodeDto {
    groupName: string;
    providerReasonCodes: ProviderReasonCodes[];
}

export interface ApplicantReasonCodeResponse {
    evaluationToken: string;
    serviceReasonCodes: ServiceReasonCodeDto[];
}

export interface TagDto {
    tagName: string;
    tagColor: string;
}

export interface WatchlistListingDetailDto {
    label?: string;
    values: string[];
}

export interface WatchlistListingDto {
    source?: string;
    url?: string;
    matchType?: string;
    details: WatchlistListingDetailDto[];
}

export interface WatchlistMatchDto {
    name?: string;
    service?: string;
    matchTypes: string[];
    matchFields: string[];
    matchStrength?: number;
    lastUpdated?: string;
    status?: string;
    listings: WatchlistListingDto[];
}

export interface WatchlistMatchesResponse {
    entityName: string;
    evaluationToken: string;
    matches: WatchlistMatchDto[];
}

export async function getApplicant(applicantId: string): Promise<Applicant> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT, {
        applicantId,
    });
    return toApplicationViewShape((await apiInstance.get<Applicant>(endpointUrl)).data);
}

// The Application view reads files and the physical address from where the cards show them, not where the API puts them.
function toApplicationViewShape(applicant: Applicant): Applicant {
    return {
        ...applicant,
        businessInfo: {
            ...applicant.businessInfo,
            files: applicant?.files,
        },
        contactInfo: {
            ...applicant?.contactInfo,
            physicalAddress: applicant.businessInfo?.address,
        },
    };
}

export async function getApplicantSubmissions(
    applicantId: string,
): Promise<ApplicantSubmissionSummary[]> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_SUBMISSIONS, {
        applicantId,
    });
    return (await apiInstance.get<ApplicantSubmissionSummary[]>(endpointUrl)).data;
}

export async function getApplicantSubmission(
    applicantId: string,
    applicantSubmissionId: string,
): Promise<Applicant> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_SUBMISSION, {
        applicantId,
        applicantSubmissionId,
    });
    return toApplicationViewShape((await apiInstance.get<Applicant>(endpointUrl)).data);
}

export async function getApplicantActivity(
    applicantId: string,
): Promise<ApplicantActivityResponse> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_ACTIVITY, {
        applicantId,
    });
    return (await apiInstance.get<ApplicantActivityResponse>(endpointUrl)).data;
}

export async function getNotificationHtml(
    applicantId: string,
    notificationId: string,
): Promise<string> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_NOTIFICATION_HTML, {
        applicantId,
        notificationId,
    });
    return (await apiInstance.get<string>(endpointUrl, { responseType: 'text' })).data;
}

export async function getApplicants(filters: GetApplicantsFilter): Promise<ApplicantResponse[]> {
    return (
        await apiInstance.post<ApplicantResponse[]>(APIRoutes.APPLICANTS, {
            productNames: filters.productNames,
            productReviewType: filters.productReviewType,
            statusFilter: filters.statusFilter,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            assigneeEmails: filters.assigneeEmails,
            includeUnassigned: filters.includeUnassigned,
            usDot: filters.usDot,
            businessNames: filters.businessNames,
            createdAtStart: filters.createdAtStart,
            createdAtEnd: filters.createdAtEnd,
            emails: filters.emails,
            feins: filters.feins,
            mcNumbers: filters.mcNumbers,
            openConditionsFilter: filters.openConditionsFilter,
            ownerNames: filters.ownerNames,
            personas: filters.personas,
            phones: filters.phones,
        })
    ).data;
}

export async function getApplicantDueDiligence(
    applicantId: string,
): Promise<ApplicantDueDiligenceData[]> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_DUE_DILIGENCE, {
        applicantId,
    });
    return (await apiInstance.get<ApplicantDueDiligenceData[]>(endpointUrl)).data;
}

export async function getApplicantVendorStatus(applicantId: string): Promise<VendorStatusResponse> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_VENDOR_STATUS, {
        applicantId,
    });
    return (await apiInstance.get<VendorStatusResponse>(endpointUrl)).data;
}

export async function getApplicantDocuments(applicantId: string): Promise<Document[]> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_DOCUMENTS, {
        applicantId,
    });
    return (await apiInstance.get<Document[]>(endpointUrl)).data;
}

export interface MissingDocumentsResponse {
    applicantId: string;
    hasAlloyJourney: boolean;
    hasMissingDocuments: boolean;
    missingDocumentCount: number;
    alloyDocumentCount: number;
    orpDocumentCount: number;
    missingDocuments: string[];
    syncedDocuments: string[];
}

export async function getApplicantMissingDocuments(
    applicantId: string,
): Promise<MissingDocumentsResponse> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_ALLOY_MISSING_DOCUMENTS, {
        applicantId,
    });
    return (await apiInstance.get<MissingDocumentsResponse>(endpointUrl)).data;
}

export async function postApplicantResubmit({
    applicantId,
    resetOverride,
    products,
}: ApplicantResubmitRequest): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_RESUBMIT, {
        applicantId,
    });
    return (
        await apiInstance.post<unknown>(endpointUrl, {
            resetOverride,
            products,
        })
    ).data;
}

export async function postRetryAlloyFactoring(applicantId: string): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_RETRY_ALLOY_FACTORING, {
        applicantId,
    });
    return (await apiInstance.post<unknown>(endpointUrl)).data;
}

export async function postRetryDdd(applicantId: string): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_RETRY_DDD, {
        applicantId,
    });
    return (await apiInstance.post<unknown>(endpointUrl)).data;
}

export async function postRefreshLoadPayStatus(applicantId: string): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_ALLOY_REFRESH_STATUS_LOADPAY, {
        applicantId,
    });
    return (await apiInstance.post<unknown>(endpointUrl)).data;
}

export async function postSyncDocumentsFromAlloy(applicantId: string): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_ALLOY_SYNC_DOCUMENTS, {
        applicantId,
    });
    return (await apiInstance.post<unknown>(endpointUrl)).data;
}

export async function postGenerateRecoveryLink({
    applicantId,
    products,
}: GenerateRecoveryLinkRequest): Promise<GenerateRecoveryLinkResponse> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_GENERATE_RECOVERY_LINK, {
        applicantId,
    });
    return (await apiInstance.post<GenerateRecoveryLinkResponse>(endpointUrl, { products })).data;
}

export async function postInternalNote({
    applicantId,
    note,
}: InternalNoteRequest): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_NOTE, {
        applicantId,
    });
    return (
        await apiInstance.post<unknown>(
            endpointUrl,
            { note },
            {
                baseURL: `${Config.BaseApiUrl}/originations`,
            },
        )
    ).data;
}

export async function postConfirmApplicantDoc({
    applicantId,
    body,
}: ConfirmApplicantDocRequest): Promise<OriginationFile> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_DOCS_CONFIRM_UPLOAD, {
        applicantId,
    });
    return (
        await apiInstance.post<OriginationFile>(endpointUrl, {
            ...body,
            fileType: body.fileType ?? FileType.Other,
        })
    ).data;
}

export async function postApplicationStatus({
    applicantId,
    product,
    status,
    reason,
    notes,
}: ApplicationStatusRequest): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_STATUS, {
        applicantId,
    });
    return (
        await apiInstance.post<unknown>(endpointUrl, {
            product,
            status,
            reason,
            notes,
        })
    ).data;
}

export const getApplicantPresignedUrl = async (request: ApplicantDocPresignRequest) => {
    // using Fetch instead of Axios.
    // Axios removes `Content-Type` headers on calls with no body:
    // https://github.com/axios/axios/issues/1535#issuecomment-390011843

    const url = generatePath(APIRoutes.APPLICANT_DOCS_PRESIGN_URL, {
        applicantId: request.applicantId,
    });

    const fetchData = await fetchMethod<ApplicantDocResponse>({
        url,
        method: 'GET',
        headers: {
            'Content-Type': request.contentType,
            fileName: request.fileName,
        },
    });

    return fetchData;
};

export const getApplicantTags = async (applicantId: string): Promise<ApplicantTagResponse[]> => {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_TAGS, {
        applicantId,
    });

    return (
        await apiInstance.get<ApplicantTagResponse[]>(endpointUrl, {
            baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
        })
    ).data;
};

export const getApplicantReasonCodes = async (
    applicantId: string,
): Promise<ApplicantReasonCodeResponse[]> => {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_REASON_CODES, {
        applicantId,
    });

    return (
        await apiInstance.get<ApplicantReasonCodeResponse[]>(endpointUrl, {
            baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
        })
    ).data;
};

export async function postAbandonApplicant({
    applicantId,
    product,
    reason,
}: AbandonApplicantRequest): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_ABANDON, {
        applicantId,
    });
    return (
        await apiInstance.post<unknown>(endpointUrl, {
            product,
            reason,
        })
    ).data;
}

export async function getApplicantNextSteps(
    applicantId: string,
): Promise<PartnersNextStepResponse> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_NEXT_STEPS, {
        applicantId,
    });
    return (
        await apiInstance.get<PartnersNextStepResponse>(endpointUrl, {
            baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
        })
    ).data;
}

export const getApplicantWatchlistMatches = async (
    applicantId: string,
): Promise<WatchlistMatchesResponse[]> => {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_WATCHLIST_MATCHES, {
        applicantId,
    });

    return (
        await apiInstance.get<WatchlistMatchesResponse[]>(endpointUrl, {
            baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
        })
    ).data;
};

export async function getApplicantRelatedAccounts(
    applicantId: string,
): Promise<RelatedAccountsResponse> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_RELATED_ACCOUNTS, {
        applicantId,
    });
    return (
        await apiInstance.get<RelatedAccountsResponse>(endpointUrl, {
            baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
        })
    ).data;
}

export async function getApplicantPointOfContacts(applicantId: string): Promise<PointOfContact[]> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_POINT_OF_CONTACTS, {
        applicantId,
    });
    return (
        await apiInstance.get<PointOfContact[]>(endpointUrl, {
            baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
        })
    ).data;
}
